<template>
  <UiComponentWrapper
    class="page"
    :class="{ 'has-footer': data.page.footer }"
  >
    <nav
      class="navigation"
      :class="{ scrolled: hasScrolled }"
    >
      <UiComponentWrapper>
        <div class="left">
          <AtomLogo />
          <div
            v-if="data.page.headerImageAfterTheLsLogo || data.page.headerTextAfterLsLogo"
            class="for-your-company"
            :class="{ reversed: data.page.headerImageType === 'logo' }"
          >
            <UiComponentImage
              v-if="data.page.headerImageAfterTheLsLogo"
              :key="`image-after-logo-${navigationImageSize}`"
              :alt="data.page.headerImageAfterTheLsLogo.alt"
              :expected-size="navigationImageSize"
              :height="data.page.headerImageAfterTheLsLogo.height"
              :lazyload="false"
              :orientation="
                data.page.headerImageAfterTheLsLogo.width
                  > data.page.headerImageAfterTheLsLogo.height
                  ? 'portrait'
                  : 'landscape'
              "
              :source="data.page.headerImageAfterTheLsLogo.url"
              :width="data.page.headerImageAfterTheLsLogo.width"
            />
            <UiComponentHeading
              v-if="data.page.headerTextAfterLsLogo"
              as="paragraph"
              :level="5"
            >
              {{ data.page.headerTextAfterLsLogo }}
            </UiComponentHeading>
            <img
              alt="Your company"
              class="customer-logo"
              src="https://livestorm.imgix.net/1127/1632240333-miro-logo.png"
            >
          </div>
        </div>
        <div class="right">
          <AtomButton
            v-if="data.page.ctaCustom"
            action="Button clicked"
            :class="{
              'is-animated':
                data.page.ctaNavigationActivateAnimation && !modalIsDisplayed,
            }"
            label="Navigation bar CTA"
            size="l"
            :to="data.page.ctaCustom.slug"
          >
            {{ data.page.ctaCustom.slot }}
          </AtomButton>
          <AtomButton
            v-else
            :action="mainButton.action"
            :class="[
              {
                'is-animated':
                  data.page.ctaNavigationActivateAnimation && !modalIsDisplayed,
              },
              mainButton.class,
            ]"
            label="Navigation bar CTA"
            size="l"
            :to="mainButton.to"
            @click="mainButton.onClick()"
          >
            {{ data.page.ctaNavigationLabel || mainButton.slot }}
          </AtomButton>
        </div>
      </UiComponentWrapper>
    </nav>
    <UiComponentWrapper class="content">
      <StructuredText
        ref="structuredText"
        :data="data.page.content"
        :render-block="renderBlock"
      />
    </UiComponentWrapper>
    <UiComponentContainer v-if="data.page.footer">
      <OrganismFooterLight />
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import Glide from '@glidejs/glide'
import inEU from '@segment/in-eu'
import { gsap } from 'gsap'
import { StructuredText } from 'vue-datocms'

import FunctionalLandingBenefitRecord from '~/components/functionals/landing-benefit-record/index.js'
import FunctionalComparisonRecord from '~/components/functionals/landing-comparison-record/index.js'
import FunctionalCsmRecord from '~/components/functionals/landing-csm-record/index.js'
import FunctionalCustomerSectionRecord from '~/components/functionals/landing-customer-section-record/index.js'
import FunctionalCustomerStoryAndKeyNumberRecord from '~/components/functionals/landing-customer-story-and-key-numbers-record/index.js'
import FunctionalCustomerStorySectionRecord from '~/components/functionals/landing-customer-story-section-record/index.js'
import FunctionalCustomersReviewRecord from '~/components/functionals/landing-customers-review-record/index.js'
import FunctionalDemoBlockReviewRecord from '~/components/functionals/landing-demo-block-record/index.js'
import FunctionalEndToEndBlockReviewRecord from '~/components/functionals/landing-end-to-end-block-record/index.js'
import FunctionalEnterpriseBlockRecord from '~/components/functionals/landing-enterprise-block-record/index.js'
import FunctionalEnterpriseServicesBlockRecord from '~/components/functionals/landing-enterprise-services-block-record/index.js'
import FunctionalFAQRecord from '~/components/functionals/landing-faq-record/index.js'
import FunctionalFormRecord from '~/components/functionals/landing-form-record/index.js'
import FunctionalFullIntegrationsRecord from '~/components/functionals/landing-full-integrations-record/index.js'
import FunctionalGetStartedBlockRecord from '~/components/functionals/landing-get-started-block-record/index.js'
import FunctionalHeroRecord from '~/components/functionals/landing-hero-record/index.js'
import FunctionalImageOrVideoFullRecord from '~/components/functionals/landing-image-video-full-record/index.js'
import FunctionalITBlockRecord from '~/components/functionals/landing-it-block-record/index.js'
import FunctionalLightIntegrationsRecord from '~/components/functionals/landing-light-integrations-record/index.js'
import FunctionalMedalsGridBlockRecord from '~/components/functionals/landing-medals-grid-block-record/index.js'
import FunctionalPricingBlockRecord from '~/components/functionals/landing-pricing-block-record/index.js'
import FunctionalProductDemoRecord from '~/components/functionals/landing-product-demo-record/index.js'
import FunctionalResourcesBlockRecord from '~/components/functionals/landing-resources-block-record/index.js'
import FunctionalSecurityBadgesRecord from '~/components/functionals/landing-security-badges-record/index.js'
import FunctionalSecurityBlockRecord from '~/components/functionals/landing-security-block-record/index.js'
import FunctionalTEIStatisticRecord from '~/components/functionals/landing-tei-statistic-record/index.js'
import FunctionalUseCasesRecord from '~/components/functionals/landing-use-cases-record/index.js'
import FunctionalWatchVideoRecord from '~/components/functionals/landing-watch-video-record/index.js'
import FunctionalMoleculeContentHeaderRecord from '~/components/functionals/molecule-content-header-record/index.js'

// Layout
definePageMeta({
  layout: 'ads',
})

// i18n
defineI18nRoute({
  paths: {
    de: '/try/[slug]',
    en: '/try/[slug]',
    es: '/try/[slug]',
    fr: '/try/[slug]',
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { state } = useConditionalPreviewMode()
const { isDesktop, isMobileOrTablet } = useDevice()
const { isEnhanced } = useEnhancedModal()
const { currentFormIndex, setFormIndex } = useForm()
const { isLogged } = useIsLoggedIn()
const { locale } = useI18n()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $lottiePlayer, $slugify } = useNuxtApp()
const { params } = useRoute()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()
const isLoading = useState('loader', () => false)

// Async Data
const { data } = await useFetch(`/api/content/pages/landing/${locale.value}`, {
  key: `page-try-[slug]-${params.slug}-${locale.value}`,
  query: {
    preview: state.preview,
  },
  transform: (input) => {
    const { allLandingPageForms, ...props } = input
    const page = allLandingPageForms.find(({ slug }) => slug === params.slug)
    return {
      ...props,
      page,
    }
  },
})

if (!data.value?.page) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
  })
}

// Meta
useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

// States
const forms = useState('forms')

// Refs
const activeCategory = ref('')
const currencies = ref(['€ EUR', '$ USD'])
const endToEndActiveIndex = ref(0)
const endToEndObserver = ref(null)
const endToEndTimelineHasBeenInitialized = ref(false)
const enterpriseServicesActiveIndex = ref(0)
const hasScrolled = ref(false)
const hasTicked = ref(false)
const isLottieAnimationLoaded = ref(false)
const isLottieFrameworkLoaded = ref(false)
const itBlockObserver = ref(null)
const itBlockHeight = ref(478)
const itBlockWidth = ref(328)
const lightIntergrationsHeight = ref(620)
const lightIntergrationsWidth = ref(1440)
const securitySectionObserver = ref(null)
const securitySectionHeight = ref(478)
const securitySectionWidth = ref(328)
const selectedCurrencyIndex = ref(0)
const signupFormHeight = ref(0)
const useCasesActiveIndex = ref(0)
const useCasesGlide = ref(null)

// Variables
let endToEndTimeline = null

// Computed Properties
const assetFullWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 48
    default:
      return 1060
  }
})

const callToActionFeatures = computed(() => {
  return [data.value.global.noCreditCard, data.value.global.noInstall]
})

const currencySymbol = computed(() => {
  return currencies.value[selectedCurrencyIndex.value].charAt(0)
})

const customizableImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return screenWidth.value - 48
    default:
      return 500
  }
})

const embedAttribute = computed(() => {
  return data.value.page.embedAttribute ? data.value.page.embedAttribute : undefined
})

const formIndex = computed(() => {
  if (data.value.page.formType?.slug === 'demo') {
    return 0
  }
  else if (data.value.page.formType?.slug === 'watch-video') {
    return 1
  }
  else {
    return -1
  }
})

const g2GridImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    default:
      return 550
  }
})

const g2MedalImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 92
    default:
      return 130
  }
})

const iframeUrl = computed(() => {
  const baseUrl = [
    `https://app.livestorm.co/signup?cta_text=${data.value.page.ctaNavigationLabel}&emails_opt_in_checked_hidden=true&lang=${locale.value}`,
  ]

  if (embedAttribute.value) {
    baseUrl.push(`&${embedAttribute.value}`)
  }

  return baseUrl.join('')
})

const mainButton = computed(() => {
  switch (true) {
    case !!data.value.page.chiliPiperCalendarUrl:
      return {
        action: 'Button clicked',
        to: '#',
        slot: data.value.page.ctaNavigationLabel,
        onClick: () => {},
      }
    case !!data.value.page.formType:
      return {
        action: data.value.page.formType.slug === 'demo' ? 'Demo clicked' : 'Button clicked',
        slot: data.value.page.ctaNavigationLabel || secondaryButton.value.slot,
        onClick: () => {
          forms.value[data.value.page.formType.slug].isVisible = true
        },
      }
    default:
      const url = [`https://${publicRuntimeConfig.appBaseUrl}/#/signup`]

      if (embedAttribute.value) {
        url.push(embedAttribute.value)
      }

      return {
        action: isLogged.value ? 'Upgrade clicked' : 'Signup clicked',
        to: url.join('?'),
        slot: data.value.page.ctaNavigationLabel || primaryButton.value.slot,
        onClick: () => {},
      }
  }
})

const modalIsDisplayed = computed(() => {
  return Object.values(forms.value).some(({ isVisible }) => isVisible)
})

const navigationImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      switch (data.value.page.headerImageType) {
        case 'flag':
          return 22
        case 'logo':
          return 28
        default:
          return 48
      }
    default:
      switch (data.value.page.headerImageType) {
        case 'g2-badge':
          return 93
        case 'flag':
          return 22
        case 'logo':
          return 28
        default:
          return 72
      }
  }
})

const primaryButton = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.global.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.global.ctaTryForFree,
      }
})

const secondaryButton = computed(() => {
  return {
    to: '#form-demo',
    action: 'Demo clicked',
    slot: data.value.global?.ctaGetDemo,
    onClick: () => {
      forms.value.demo.isVisible = true
    },
  }
})

const securityIconSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 146
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return 146
    default:
      return 172
  }
})

// Watchers
watch(() => screenWidth.value, () => {
  const $lightIntegrations = document.querySelector('.light-integrations')
  lightIntergrationsHeight.value = $lightIntegrations?.offsetHeight || 620
  lightIntergrationsWidth.value = $lightIntegrations?.offsetWidth || 1440
})

// Methods
function createEndToEndTimeline() {
  const $endToEnd = document.querySelector('.end-to-end')
  const $cursor = $endToEnd.querySelector('.head .cursor')
  const $progress = $cursor.querySelector('.progress')
  const $items = $endToEnd.querySelectorAll('.head .item')
  const $slider = $endToEnd.querySelector('.body .slider')
  const backgroundColors = data.value.componentEndToEnd.steps.map(({ backgroundColor }) => (backgroundColor && backgroundColor.hex) || '#FFFFFF')
  const colors = data.value.componentEndToEnd.steps.map(({ color }) => (color && color.hex) || '#FFFFFF')

  if (!endToEndTimelineHasBeenInitialized.value) {
    [...$endToEnd.querySelectorAll('video')].forEach(($video) => {
      $video.setAttribute('muted', '')
      $video.setAttribute('playsinline', '')
    })

    gsap.set($endToEnd, {
      backgroundColor:
        (backgroundColors && backgroundColors[0]) || '#FFFFFF',
    })
    gsap.set($cursor, { width: $items[0].offsetWidth })
    gsap.set($progress, {
      backgroundColor: (colors && colors[0]) || '#FFFFFF',
    })
    gsap.set($slider, { x: 0 })

    endToEndTimelineHasBeenInitialized.value = true
  }

  if (endToEndTimeline) {
    endToEndTimeline.kill()
    endToEndTimeline = null
  }

  endToEndTimeline = gsap.timeline({
    onStart: () => {
      const $videos = $slider
        .querySelectorAll('.item')
        [endToEndActiveIndex.value].querySelectorAll('video')
      if ($videos && $videos.length > 0) {
        [...$videos].forEach(($video) => {
          $video.pause()
          $video.currentTime = 0
          $video.play()
        })
      }
    },
    onComplete: () => {
      const newIndex
        = (endToEndActiveIndex.value + 1)
          % data.value.componentEndToEnd.steps.length
      setEndToEndIndex(newIndex)
    },
  })
  endToEndTimeline.to($progress, {
    width: '100%',
    duration: 8,
    ease: 'none',
  })
}

function enableEnhancedModal() {
  isEnhanced.value = true
}

function onObserveEndToEndBlock([{ isIntersecting }]) {
  if (isIntersecting) {
    endToEndTimeline.play()
  }
  else {
    endToEndTimeline.pause()
  }
}

function onObserveSecuritySection() {
  const { offsetHeight, offsetWidth }
    = document.querySelector('.security .inner')
  securitySectionHeight.value = offsetHeight
  securitySectionWidth.value = offsetWidth
}

function onObserveItBlock() {
  const { offsetHeight, offsetWidth }
    = document.querySelector('.it .inner')
  itBlockHeight.value = offsetHeight
  itBlockWidth.value = offsetWidth
}

function onMessage() {
  if (event.data.kind === 'signup-height') {
    signupFormHeight.value = event.data.value
  }
}

function onReady() {
  isLottieAnimationLoaded.value = true

  if (isMobileOrTablet) {
    const player = document.querySelector('lottie-player')
    player.seek('100%')
    player.play()
  }
}

function onScroll() {
  if (!hasTicked.value) {
    hasTicked.value = true
    window.requestAnimationFrame(() => {
      hasTicked.value = false
      hasScrolled.value = window.scrollY > 0
    })
  }
}

async function onSubmitted(currentForm, submittedValues) {
  // Trigger event to Google Tag Manager
  if (window.dataLayer && currentForm.gtmEvent) {
    const { v4: uuidv4 } = await import('uuid')
    dataLayer.push({ event: `${currentForm.gtmEvent}.${uuidv4()}` }) // Send event to GTM
  }

  // Trigger event to Segment
  const properties = currentForm.segmentWhitelistedProperties
    ? currentForm.segmentWhitelistedProperties
        .split(',')
        .map(value => value.trim())
        .reduce((accumulator, value) => {
          return {
            ...accumulator,
            [value]: submittedValues[value] ? submittedValues[value] : undefined,
          }
        }, {})
    : submittedValues

  await track(currentForm.segmentEvent || 'Form submitted', {
    ...properties,
    category: 'Form',
    form_id: currentForm.formId,
    label: currentForm.segmentLabel || currentForm.segmentEvent || 'Form submitted',
    locale: locale.value,
    title: window.document.title,
  })

  if (currentForm.redirectUrl) {
    window.location = `${publicRuntimeConfig.baseUrl}${currentForm.redirectUrl}`
  }
  else if (currentForm.slug === 'demo') {
    isLoading.value = true
    window.setTimeout(() => {
      forms.value.demo.hasBeenSubmitted = true
    }, 1000)
  }
  else {
    forms.value[currentForm.slug].hasBeenSubmitted = true
  }
}

function renderBlock({ record }) {
  switch (record.__typename) {
    // Block > Benefits
    case 'LandingBenefitRecord':
      return h(FunctionalLandingBenefitRecord, {
        callToAction: mainButton.value,
        page: data.value.page,
        record,
      })

    // Block > Software Comparison Table
    case 'LandingComparisonRecord':
      return h(FunctionalComparisonRecord, {
        activeCategory: activeCategory.value,
        comparisons: {
          allFeaturesComparisons: data.value.allFeaturesComparisons,
          allFeaturesCategories: data.value.allFeaturesCategories,
          pageComparison: data.value.pageComparison,
        },
        record,
      })

    // Block > CSM
    case 'LandingCsmBlockRecord':
      return h(FunctionalCsmRecord, {
        componentCsmSection: data.value.componentCsmSection,
        componentGlobal: data.value.global,
        record,
      })

    // Block > Customers
    case 'LandingCustomerSectionRecord':
      return h(FunctionalCustomerSectionRecord, {
        markdown: $md,
        page: data.value.page,
        record,
      })

    // Block > Customer Stories with key numbers
    case 'LandingCustomerStoryAndKeyNumberRecord':
      return h(FunctionalCustomerStoryAndKeyNumberRecord, {
        global: data.value.global,
        record,
        to: localePath('customers'),
      })

    // Block > Customer Stories
    case 'LandingCustomerStorySectionRecord':
      return h(FunctionalCustomerStorySectionRecord, {
        callback: () => {
          forms.value.demo.isVisible = true
        },
        global: data.value.global,
        primaryButton: primaryButton.value,
        record,
        secondaryButton: secondaryButton.value,
      })

    // Block > Customer Quote
    case 'LandingCustomersReviewRecord':
      return h(FunctionalCustomersReviewRecord, {
        componentCertification: data.value.componentCertification,
        record,
      })

    // Block > Demo
    case 'LandingDemoBlockRecord':
      return h(FunctionalDemoBlockReviewRecord, {
        componentDemo: data.value.componentDemo,
        global: data.value.global,
        mainButton: mainButton.value,
        record,
      })

    // Block > End to end
    case 'LandingEndToEndBlockRecord':
      return h(FunctionalEndToEndBlockReviewRecord, {
        callback: setEndToEndIndex,
        componentEndToEnd: data.value.componentEndToEnd,
        endToEndActiveIndex: endToEndActiveIndex.value,
        record,
      })

    // Block > Enterprise
    case 'LandingEnterpriseBlockRecord':
      return h(FunctionalEnterpriseBlockRecord, {
        isDesktop,
        isLottieAnimationLoaded: isLottieAnimationLoaded.value,
        componentEnterprise: data.value.componentEnterprise,
        global: data.value.global,
        record,
      })

    // Block > Enterprise Services
    case 'LandingEnterpriseServicesBlockRecord':
      return h(FunctionalEnterpriseServicesBlockRecord, {
        callback: setEnterpriseServiceIndex,
        componentEnterpriseServices: data.value.componentEnterpriseServices,
        enterpriseServicesActiveIndex: enterpriseServicesActiveIndex.value,
        primaryButton: primaryButton.value,
        record,
      })

    // Block > FAQ
    case 'LandingFaqRecord':
      return h(FunctionalFAQRecord, {
        global: data.value.global,
        primaryButton: primaryButton.value,
        record,
      })

    // Block > Form (Agency, Demo, Referral, Signup)
    case 'LandingFormRecord':
      return h(FunctionalFormRecord, {
        currentForm: data.value.page.formType,
        forms: forms.value,
        global: data.value.global,
        isLogged: isLogged.value,
        markdown: $md,
        onSubmitted,
        page: data.value.page,
        primaryButton: primaryButton.value,
        record,
        signupForm: {
          height: signupFormHeight.value,
          url: iframeUrl.value,
        },
      })

    // Block > Full Integrations
    case 'LandingFullIntegrationRecord':
      return h(FunctionalFullIntegrationsRecord, {
        record,
      })

    // Block > Get Started
    case 'LandingGetStartedBlockRecord':
      return h(FunctionalGetStartedBlockRecord, {
        callToActionFeatures: callToActionFeatures.value,
        primaryButton: primaryButton.value,
        record,
        secondaryButton: secondaryButton.value,
      })

    // Block > Hero
    case 'LandingHeroRecord':
      return h(FunctionalHeroRecord, {
        allForms: data.value.allForms,
        clientWidth: screenWidth.value,
        currentForm: data.value.page.formType,
        componentCertification: data.value.componentCertification,
        currentFormIndex: currentFormIndex.value,
        customersReviewLogoSection: data.value.customersReviewLogoSection,
        customizableImageSize: customizableImageSize.value,
        forms: forms.value,
        isLogged: isLogged.value,
        mainButton: mainButton.value,
        markdown: $md,
        modelForm: data.value.modelForm,
        onSubmitted,
        page: data.value.page,
        primaryButton: primaryButton.value,
        record,
        secondaryButton: secondaryButton.value,
        setFormIndex,
        signupForm: {
          height: signupFormHeight.value,
          url: iframeUrl.value,
        },
        teiStatistics: data.value.teiStatistic,
      })

    // Block > Image or Video Full
    case 'LandingImageVideoFullRecord':
      return h(FunctionalImageOrVideoFullRecord, {
        assetFullWidth: assetFullWidth.value,
        record,
      })

    // Block > IT
    case 'LandingItBlockRecord':
      return h(FunctionalITBlockRecord, {
        clientWidth: screenWidth.value,
        componentIT: data.value.componentIT,
        componentSecurity: data.value.componentSecurity,
        global: data.value.global,
        itBlockHeight: itBlockHeight.value,
        itBlockWidth: itBlockWidth.value,
        record,
      })

    // Block > Light Integrations
    case 'LandingLightIntegrationRecord':
      return h(FunctionalLightIntegrationsRecord, {
        lightIntergrationsHeight: lightIntergrationsHeight.value,
        lightIntergrationsWidth: lightIntergrationsWidth.value,
        record,
      })

    // Block > Medals Grid
    case 'LandingMedalsGridBlockRecord':
      return h(FunctionalMedalsGridBlockRecord, {
        g2GridImageWidth: g2GridImageWidth.value,
        g2MedalImageWidth: g2MedalImageWidth.value,
        record,
      })

    // Block > Pricing
    case 'LandingPricingBlockRecord':
      return h(FunctionalPricingBlockRecord, {
        componentPricingCard: data.value.componentPricingCard,
        currencySymbol: currencySymbol.value,
        global: data.value.global,
        pricing: data.value.pricing,
        primaryButton: primaryButton.value,
        record,
        secondaryButton: secondaryButton.value,
        wording: data.value.wording,
      })

    // Block > Product Demo
    case 'LandingProductDemoRecord':
      return h(FunctionalProductDemoRecord)

    // Block > Resources
    case 'LandingResourcesBlockRecord':
      return h(FunctionalResourcesBlockRecord, {
        allResourceTypes: data.value.allModelOfferTypes,
        localePath,
        record,
        slugify: $slugify,
      })

    // Block > Security Badge
    case 'LandingSecurityBadgeRecord':
      return h(FunctionalSecurityBadgesRecord, {
        componentSecurityBadges: data.value.securityBadge,
        record,
      })

    // Block > Security
    case 'LandingSecurityBlockRecord':
      return h(FunctionalSecurityBlockRecord, {
        componentSecurity: data.value.componentSecurity,
        global: data.value.global,
        record,
        securityIconSize: securityIconSize.value,
        securitySectionHeight: securitySectionHeight.value,
        securitySectionWidth: securitySectionWidth.value,
      })

    // Block > TEI Statistics
    case 'LandingTeiStatisticRecord':
      return h(FunctionalTEIStatisticRecord, {
        record,
        statistics: data.value.teiStatistic,
      })

    // Block > Use Cases
    case 'LandingUseCaseRecord':
      return h(FunctionalUseCasesRecord, {
        mainButton: mainButton.value,
        page: data.value.page,
        record,
        track,
        useCasesActiveIndex: useCasesActiveIndex.value,
        useCasesGlide: useCasesGlide.value || {},
      })

    // Block > Watch Video
    case 'LandingWatchVideoRecord':
      return h(FunctionalWatchVideoRecord, {
        global: data.value.global,
        primaryButton: primaryButton.value,
        record,
      })

    // Block > Content Header
    case 'MoleculeContentHeaderRecord':
      return h(FunctionalMoleculeContentHeaderRecord, {
        record,
      })
    default:
      return null
  }
}

function setEndToEndIndex(index) {
  if (endToEndActiveIndex.value === index) {
    return
  }

  endToEndTimeline.pause()
  endToEndActiveIndex.value = index

  const $endToEnd = document.querySelector('.end-to-end')
  const $cursor = $endToEnd.querySelector('.head .cursor')
  const $progress = $cursor.querySelector('.progress')
  const $items = $endToEnd.querySelectorAll('.head .item')
  const $slider = $endToEnd.querySelector('.body .slider')
  const $sliderItems = $slider.querySelectorAll('.item')
  const $videos = $slider.querySelectorAll('video')
  const backgroundColors = data.value.componentEndToEnd.steps.map(({ backgroundColor }) => (backgroundColor && backgroundColor.hex) || '#FFFFFF')
  const colors = data.value.componentEndToEnd.steps.map(({ color }) => (color && color.hex) || '#FFFFFF')

  if ($videos && $videos.length > 0) {
    [...$videos].forEach(($video) => {
      $video.pause()
      $video.currentTime = 0
    })
  }

  gsap.to($endToEnd, {
    backgroundColor: backgroundColors[index],
    duration: 0.6,
    ease: 'power2.inOut',
  })
  gsap.to($slider, {
    x: `-${$sliderItems[index].offsetLeft}px`,
    duration: 0.6,
    ease: 'power2.inOut',
  })
  gsap.to(
    [...$sliderItems].filter((_, i) => i < index),
    {
      autoAlpha: 0,
      duration: 0.4,
      ease: 'power2.inOut',
    },
  )
  gsap.to(
    [...$sliderItems].filter((_, i) => i >= index),
    {
      autoAlpha: 1,
      duration: 0.6,
      ease: 'power2.inOut',
    },
  )
  gsap.to($cursor, {
    width: $items[index].offsetWidth,
    x: $items[index].offsetLeft - 4,
    duration: 0.6,
    ease: 'power2.inOut',
  })
  gsap.to($progress, {
    backgroundColor: colors[index],
    width: '0%',
    duration: 0.6,
    ease: 'power2.inOut',
    onComplete: () => {
      createEndToEndTimeline()
      endToEndTimeline.play()
    },
  })
}

function setEnterpriseServiceIndex(index) {
  if (enterpriseServicesActiveIndex.value === index) {
    return
  }
  enterpriseServicesActiveIndex.value = index
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage)
  window.removeEventListener('scroll', onScroll)

  endToEndObserver.value?.disconnect()
  endToEndObserver.value = null

  endToEndTimeline?.kill()
  endToEndTimeline = null

  itBlockObserver.value?.unobserve(document.querySelector('.page .it .inner'))

  securitySectionObserver.value?.unobserve(document.querySelector('.page .security .inner'))

  useCasesGlide.value?.destroy()
})

onMounted(() => {
  window.addEventListener('message', onMessage)
  window.addEventListener('scroll', onScroll)

  page(data.value.page.trackName || data.value.page.trackNameFallback)
  setFormIndex(formIndex.value)

  if (data.value.page.extendFormDemo) {
    enableEnhancedModal()
  }

  if (!inEU()) {
    selectedCurrencyIndex.value = 1
  }

  if (document.querySelector('.page .end-to-end')) {
    createEndToEndTimeline()

    endToEndObserver.value = new IntersectionObserver(onObserveEndToEndBlock)
    endToEndObserver.value.observe(document.querySelector('.page .end-to-end'))
  }

  if (document.querySelector('.it .inner')) {
    itBlockObserver.value = new ResizeObserver(onObserveItBlock)
    itBlockObserver.value.observe(document.querySelector('.page .it .inner'))
  }

  if (document.querySelector('.page .our-enterprise-plan')) {
    $lottiePlayer.init(() => {
      isLottieFrameworkLoaded.value = true
      nextTick(() => {
        const player = document.querySelector('lottie-player')
        player.addEventListener('ready', onReady)
      })
    })
  }

  if (document.querySelector('.page .security .inner')) {
    securitySectionObserver.value = new ResizeObserver(onObserveSecuritySection)
    securitySectionObserver.value.observe(document.querySelector('.page .security .inner'))
  }

  if (document.querySelector('.page .use-cases .desktop .glide')) {
    document
      .querySelector('.page .use-cases .desktop .glide .inner')
      .setAttribute('data-glide-el', 'track')

    useCasesGlide.value = new Glide('.use-cases .desktop .glide', {
      type: 'slider',
      gap: 0,
      perView: 1,
    })
    useCasesGlide.value.on('run.after', () => {
      useCasesActiveIndex.value = useCasesGlide.value.index
    })
    useCasesGlide.value.mount()
  }
})
</script>

<style lang="postcss" scoped>
@import "~/components/functionals/landing-benefit-record/styles.css";
@import "~/components/functionals/landing-comparison-record/styles.css";
@import "~/components/functionals/landing-customer-section-record/styles.css";
@import "~/components/functionals/landing-customer-story-and-key-numbers-record/styles.css";
@import "~/components/functionals/landing-customer-story-section-record/styles.css";
@import "~/components/functionals/landing-customers-review-record/styles.css";
@import "~/components/functionals/landing-demo-block-record/styles.css";
@import "~/components/functionals/landing-end-to-end-block-record/styles.css";
@import "~/components/functionals/landing-enterprise-block-record/styles.css";
@import "~/components/functionals/landing-enterprise-services-block-record/styles.css";
@import "~/components/functionals/landing-faq-record/styles.css";
@import "~/components/functionals/landing-form-record/styles.css";
@import "~/components/functionals/landing-full-integrations-record/styles.css";
@import "~/components/functionals/landing-get-started-block-record/styles.css";
@import "~/components/functionals/landing-hero-record/styles.css";
@import "~/components/functionals/landing-image-video-full-record/styles.css";
@import "~/components/functionals/landing-it-block-record/styles.css";
@import "~/components/functionals/landing-light-integrations-record/styles.css";
@import "~/components/functionals/landing-medals-grid-block-record/styles.css";
@import "~/components/functionals/landing-pricing-block-record/styles.css";
@import "~/components/functionals/landing-resources-block-record/styles.css";
@import "~/components/functionals/landing-security-block-record/styles.css";
@import "~/components/functionals/landing-tei-statistic-record/styles.css";
@import "~/components/functionals/landing-use-cases-record/styles.css";
@import "~/components/functionals/landing-watch-video-record/styles.css";
@import "~/components/functionals/molecule-content-header-record/styles.css";

.page {
  @apply relative flex-col items-center pb-8;

  @screen md {
    @apply pb-16;
  }

  @screen lg {
    @apply pb-20;
  }

  @screen xxxl {
    @apply pb-26;
  }

  &::before {
    @apply absolute top-0 flex h-[100vh] w-full content-[''] -z-10;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%),
      #f0f4ff;
  }

  &.has-footer {
    @apply pb-0 !important;
  }

  :deep(h1) {
    @apply text-3xl leading-10;
  }

  .navigation {
    @apply sticky top-0 flex justify-center w-full px-4 pt-8 pb-4 border-b border-b-transparent z-20 transition-colors duration-300 ease-in-out;

    @screen md {
      @apply pt-4 px-6;
    }

    @screen xl {
      @apply px-0;
    }

    &.scrolled {
      @apply bg-white border-grey-blue-200;
    }

    > div {
      @apply w-full h-full justify-between items-center max-w-[1280px] box-border;

      .left {
        @apply flex flex-col;

        @screen md {
          @apply flex-row;
        }

        .logo {
          @apply w-40 my-auto;

          @screen md {
            @apply pb-1;
          }

          img {
            @apply w-full;
          }
        }

        .for-your-company {
          @apply flex items-center mt-4;

          @screen md {
            @apply ml-4 mt-0;
          }

          &.display-logo {
            .h5 {
              :deep(strong) {
                @apply hidden;
              }
            }

            .customer-logo {
              @apply flex;
            }
          }

          &.reversed {
            .h5 {
              @apply order-first mr-4;
            }
          }

          picture {
            @apply mr-4 rounded-none;
          }

          .h5 {
            @apply leading-none;
          }

          .customer-logo {
            @apply hidden ml-4 h-[30px] w-auto;
          }
        }
      }

      .right {
        @apply hidden;

        @screen md {
          @apply flex flex-row;
        }

        .button {
          &.is-animated {
            @screen md {
              @apply animate-[elastic_4s_infinite_ease-in-out];
              animation-delay: 2s;
            }
          }
        }
      }
    }
  }

  > :deep(.content) {
    @apply w-full overflow-hidden;

    > div {
      @apply flex flex-col items-center w-full;
    }
  }

  :deep(.companies-trust) {
    .companies {
      @apply mt-4;

      @screen mdminus {
        @apply mt-6;
      }

      @screen mdplus {
        @apply mt-8;
      }

      @screen xl {
        @apply mt-4;
      }

      .logo {
        @apply items-center;
        height: 60px;

        @screen md {
          height: 46px;
        }

        @screen xl {
          height: 52px;
        }
      }
    }

    .headline {
      @apply order-first mt-0;

      p,
      span {
        @apply whitespace-normal;
      }
    }
  }

  :deep(.chilipiper) {
    @apply justify-center mt-26 py-20 bg-blizzard-100;

    @screen md {
      @apply py-34;
    }

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }

    .inner {
      @apply flex-col;

      @screen lg {
        @apply flex-row justify-between items-center;
      }

      .copy {
        @apply flex-col;

        @screen lg {
          @apply w-span4;
        }

        .h2 + p {
          @apply mt-4 text-grey-blue-600;
        }
      }

      .embed {
        @apply relative mt-8 shadow-light3 rounded-[24px] overflow-hidden;

        @screen md {
          @apply rounded-4xl;
        }

        @screen lg {
          @apply w-span8 mt-0;
        }

        &::before {
          @apply absolute w-8 h-8 bg-white z-10;
          content: "";
          right: 16px;
          top: 16px;
        }

        &::after {
          @apply absolute left-0 right-0 bottom-0 w-full z-10;
          background: #f8f9fa;
          content: "";
          height: 42px;
        }

        iframe {
          @apply m-0 p-0 border-none shadow-none rounded-none;
        }
      }
    }
  }

  :deep(.header + *) {
    @apply mt-0 !important;
  }

  :deep(.customer-story-section + *) {
    @apply mt-10 !important;

    @screen md {
      @apply mt-24 !important;
    }
  }

  :deep(.benefit.background-livestorm-blue + .use-cases),
  :deep(.benefit.background-pale-blue + .use-cases) {
    @apply mt-0;

    @screen md {
      @apply mt-[22px];
    }
  }

  :deep(.benefit.background-livestorm-blue
      + .benefit.background-livestorm-blue),
  :deep(.benefit.background-livestorm-blue + .benefit.background-pale-blue),
  :deep(.benefit.background-pale-blue + .benefit.background-livestorm-blue),
  :deep(.benefit.background-pale-blue + .benefit.background-pale-blue) {
    @apply pt-0;
  }

  :deep(.chilipiper + .light-integrations),
  :deep(.customer-stories + .light-integrations),
  :deep(.customer-story-section + .light-integrations),
  :deep(.end-to-end + .light-integrations),
  :deep(.enterprise-services + .light-integrations),
  :deep(.get-a-demo + .light-integrations),
  :deep(.get-started + .light-integrations),
  :deep(.comparisons + .light-integrations),
  :deep(.customers + .light-integrations),
  :deep(.faq + .light-integrations),
  :deep(.full-integrations + .light-integrations),
  :deep(.g2-grid + .light-integrations),
  :deep(.image-or-video-full + .light-integrations),
  :deep(.our-enterprise-plan + .light-integrations),
  :deep(.pricing + .light-integrations),
  :deep(.product-tour + .light-integrations),
  :deep(.reviews + .light-integrations),
  :deep(.tei + .light-integrations),
  :deep(.resources + .light-integrations),
  :deep(.security-badges + .light-integrations),
  :deep(.security + .light-integration),
  :deep(.use-cases + .light-integrations),
  :deep(.watch-a-video + .light-integrations) {
    @apply mt-14;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }
  }

  :deep(.chilipiper + .form),
  :deep(.customer-stories + .form),
  :deep(.customer-story-section + .form),
  :deep(.end-to-end + .form),
  :deep(.enterprise-services + .form),
  :deep(.get-a-demo + .form),
  :deep(.get-started + .form),
  :deep(.comparisons + .form),
  :deep(.customers + .form),
  :deep(.faq + .form),
  :deep(.full-integrations + .form),
  :deep(.g2-grid + .form),
  :deep(.image-or-video-full + .form),
  :deep(.our-enterprise-plan + .form),
  :deep(.pricing + .form),
  :deep(.product-tour + .form),
  :deep(.reviews + .form),
  :deep(.tei + .form),
  :deep(.resources + .form),
  :deep(.security-badges + .form),
  :deep(.security + .form),
  :deep(.use-cases + .form),
  :deep(.watch-a-video + .form) {
    @apply mt-14;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }
  }
}

@media screen and (min-width: 512px) {
  .page {
    :deep(.chilipiper) {
      .inner {
        .embed {
          &::before {
            background: #f8f9fa;
          }
        }
      }
    }
  }
}

@keyframes elastic {
  0% {
    transform: translateX(0px);
  }
  5.865% {
    transform: translateX(-26.4px);
  }
  9.4845% {
    transform: translateX(10.96px);
  }
  14.8% {
    transform: translateX(-6.62px);
  }
  18.975% {
    transform: translateX(1.94px);
  }
  22.5% {
    transform: translateX(0px);
  }
}
</style>
